import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  getPendingSurveyToken,
  savePendingSurveyToken,
  uploadToS3,
  cx
} from "../utils.js";
import profileUploadImage from "../assets/profile-upload.png";
import profileAnalysisImage from "../assets/profile-analysis.png";
import profileShirtImage from "../assets/profile-shirt.png";
import fullBodyReferenceImage from "../assets/full-body-reference.png";
import faceReferenceImage from "../assets/face-reference.png";
import modalImageOne from "../assets/modal-image-one.png";
import modalImageTryOn from "../assets/modal-image-try-on.png";
import modalImageTryOnTwo from "../assets/modal-image-try-on-two.png";
import modalImageTwo from "../assets/modal-image-two.jpg";
import modalImageAiOne from "../assets/modal-image-ai-one.png";
import modalImageAiTwo from "../assets/modal-image-ai-two.png";
import modalImageAiThree from "../assets/modal-image-ai-three.png";
import modalImageAiFour from "../assets/modal-image-ai-four.png";
import seeAllMatches from "../assets/see-all-matches.png";
import toast from "react-hot-toast";
import { ReactComponent as GoogleIcon } from "./../assets/svgs/login-google-icon.svg";
import { ReactComponent as EvenessLogo } from "./../assets/svgs/eveness-logo.svg";
import { ReactComponent as BodyShapeIcon } from "./../assets/svgs/body-shape.svg";
import { ReactComponent as ColorAnalysisIcon } from "./../assets/svgs/color-analysis.svg";
import { ReactComponent as FaceShapeIcon } from "./../assets/svgs/face-shape-icon.svg";
import { ReactComponent as HeightProportionsIcon } from "./../assets/svgs/height-proportions-icon.svg";
import { ReactComponent as VirtualTryOnIcon } from "./../assets/svgs/virtual-try-on-icon.svg";
import { ReactComponent as AskGptIcon } from "./../assets/svgs/ask-gpt-icon.svg";
import { ReactComponent as SaveToBoardsIcon } from "./../assets/svgs/save-to-boards.svg";
import { ReactComponent as FindSimilarItemsIcon } from "./../assets/svgs/find-similar-items.svg";
import ProfileMeasurementsForm from "./ProfileMeasurementsForm.js";

import {
  usePostAnalyzeFaceMutation,
  usePostMarketplaceSurveySuggestionsMutation,
  usePostPublicAnalyzeFaceMutation,
  usePostPublicUploadImageMutation,
  useProfileMeasurementsStoreUpdateMutation,
  usePublicProfileMeasurementsStoreUpdateMutation,
} from "../store/api.ts";

const ANALYSIS_TARGETS = {
  body: 100,
  palette: 100,
  face: 100,
};

const normalizePublicGender = (gender) => {
  const value = String(gender || "")
    .trim()
    .toLowerCase();

  if (value === "male") {
    return "male";
  }

  if (value === "female") {
    return "female";
  }

  return "other";
};

const convertCmToImperialHeight = (heightCm) => {
  const totalInches = Math.round(Number(heightCm) / 2.54);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;

  return `${feet}.${String(inches).padStart(2, "0")}`;
};

const convertKgToImperialWeight = (weightKg) => {
  return String(Math.round(Number(weightKg) * 2.20462));
};

const formatCmToFeetInches = (heightCm) => {
  const totalInches = Math.round(Number(heightCm) / 2.54);

  if (!Number.isFinite(totalInches)) return "";

  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;

  return `${feet}"${inches}'`;
};

const normalizeSuggestionProducts = (products) => {
  if (!Array.isArray(products)) {
    return [];
  }

  return products.map((product) => ({
    id: product?.id,
    name: product?.product_name || "Product",
    image: product?.image_url || "",
    brand: product?.brand || "",
    price: product?.price || "",
    slug: product?.slug || "",
    productUrl: product?.product_url || "",
    garmentType: product?.garment_type || "",
    matchPercentage:
      product?.match_percentage !== null &&
      product?.match_percentage !== undefined
        ? Math.round(Number(product.match_percentage))
        : null,
    recommendedSize: product?.recommended_size || null,
    sizeBasis: product?.size_basis || null,
    recommendedSizeLabel: formatRecommendedSize(
      product?.recommended_size,
      product?.size_basis,
    ),
  }));
};

const formatRecommendedSize = (recommendedSize, sizeBasis) => {
  if (!recommendedSize) return "";

  const normalizedBasis = String(sizeBasis || "").toLowerCase();

  if (normalizedBasis === "waist") {
    const waist = Number(recommendedSize);
    return `${Number.isFinite(waist) ? Math.round(waist) : recommendedSize} in`;
  }

  return `${recommendedSize} Recommended`;
};

const getProfileApiError = (error) => {
  const errorData = error?.data;

  if (typeof errorData === "string") {
    return errorData;
  }

  if (typeof errorData?.detail === "string") {
    return errorData.detail;
  }

  if (typeof errorData?.error === "string") {
    return errorData.error;
  }

  if (typeof errorData?.message === "string") {
    return errorData.message;
  }

  return error?.message || "Something went wrong while analyzing your profile.";
};


function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
    </svg>
  );
}


export const GradientButton = ({ children, className = "", ...props }) => {
  return (
    <button
      type="button"
      className={cx(
        "flex h-[42px] max-w-[415px] items-center justify-center rounded-[5px] bg-gradient-to-r from-[#E9AEA1] via-[#DF9188] to-[#B76570] px-[92px] font-helvena text-[14px] font-normal text-white shadow-sm transition hover:brightness-[0.98] disabled:cursor-not-allowed disabled:opacity-55",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function StepShell({
  children,
  dark = false,
  width = "max-w-[690px]",
  onClose,
}) {
  return (
    <div className={cx("relative mx-auto w-full", width)}>
      <div
        className={cx(
          "relative overflow-hidden rounded-[5px] border shadow-2xl",
          dark ? "border-[#1A1A1A] bg-[#1A1A1A]" : "border-[#E7E7E7] bg-white",
        )}
      >
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className={cx(
              [
                "absolute right-3 top-3 z-50",
                "flex h-9 w-9 items-center justify-center",
                "rounded-full shadow-lg transition",
              ].join(" "),
              dark
                ? "bg-white/15 text-white hover:bg-white/25"
                : "bg-white text-[#292929] hover:bg-[#F4F4F4]",
            )}
            aria-label="Close profile popup"
          >
            <CloseIcon />
          </button>
        ) : null}

        <div
          className={[
            "max-h-[calc(100dvh-2rem)]",
            "overflow-x-hidden overflow-y-auto overscroll-contain",
            "[scrollbar-gutter:stable]",
            "sm:max-h-[calc(100dvh-5rem)]",
          ].join(" ")}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function HeroStep({onNext, onSignIn, onClose, isRestartMode = false, showGuestAuthPrompt = true}) {
  return (
    <StepShell width="max-w-[1020px]" onClose={onClose}>
      <div className="px-5 pb-5 pt-7 sm:px-8">
        <div className="text-center">
          <h2 className="font-helvena text-[20px] md:text-[24px] font-medium tracking-[-2%] text-[#313131] text-center">
            {isRestartMode
              ? "Let's Analyze You Again"
              : "Find what actually looks best on you."}
          </h2>
          <p className="mx-auto mt-1 max-w-[400px] font-helvena text-[10px] leading-[20px] text-[#8A8580] sm:text-[12px]">
            {isRestartMode
              ? "Simply upload a photo of yourself. Our AI analyzes your unique features—from color palette to proportions—to recommend pieces that truly"
              : "Upload one photo. We measure your color analysis and proportions, then score every piece on how it looks on you, not on the model."}
          </p>
        </div>

        <div className="mx-auto mt-5 w-full max-w-[300px] pb-3 md:max-w-[900px]">
          <div className="flex w-full justify-center md:grid md:grid-cols-[0.98fr_1fr_1fr] md:gap-3">
            {/* First card: personalized match */}
            <div className="hidden overflow-hidden rounded-[5px] bg-[#EFE7E2] p-2 md:block md:w-auto">
              <div className="relative h-[314px] overflow-hidden rounded-[3px] border-[3px] border-b-[15px] border-white bg-[#E9E2DC]">
                <img
                  src={modalImageTwo}
                  alt="Personalized match preview"
                  className="h-full w-full object-cover object-top"
                />

                <div className="absolute bottom-[70px] left-1/2 z-10 flex h-7 w-[126px] -translate-x-1/2 items-center justify-center rounded-[4px] bg-white shadow-sm">
                  <span className="font-inter text-[11px] font-normal text-[#5CB7A3]">
                    86% match on you
                  </span>
                </div>

                <div className="absolute bottom-[6px] left-[9px] right-[9px] rounded-[7px] bg-white shadow-sm">
                  <div className="grid grid-cols-3 divide-x divide-[#ECECEC]">
                    {[
                      ["Overall Fit", "88"],
                      ["Color Analysis", "79"],
                      ["Body Shape", "91"],
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className="flex h-[58px] flex-col items-center justify-center"
                      >
                        <span className="font-inter text-[9px] font-normal text-[#111111]">
                          {label}
                        </span>

                        <span className="mt-[5px] flex h-[25px] w-[25px] items-center justify-center rounded-full border-2 border-[#12B88C] bg-[#EFFFF9] font-inter text-[9px] font-semibold text-[#009B72]">
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute bottom-[2px] left-[2px] right-[4px] flex items-center justify-between font-inter text-[8px] font-semibold text-[#111111]">
                  <span>Reformation</span>
                  <span>$250</span>
                </div>
              </div>
            </div>

            {/* Second card: AI try-on */}
            <div className="w-full max-w-[290px] overflow-hidden rounded-[5px] sm:max-w-[300px] md:max-w-[400px]">
              <div className="relative h-[342px] bg-[#F6F6F6]">
                <img
                  src={modalImageOne}
                  alt="Virtual try-on preview"
                  className="h-full w-full rounded-[7px] object-cover object-top"
                />

                <div className="absolute bottom-[10px] left-[10px] flex flex-col items-end gap-2">
                  <div className="flex h-[30px] min-w-[110px] items-center rounded-[5px] bg-white px-[10px] shadow-sm">
                    <p className="font-inter text-[11px] font-normal leading-none text-[#333333]">
                      AI Try-on before you buy
                    </p>
                  </div>

                  <div className="flex flex-row gap-2">
                    <div className="mr-[18px] h-[62px] w-[62px] overflow-hidden rounded-[5px] bg-white shadow-sm">
                      <img
                        src={modalImageTryOn}
                        alt="Dress preview"
                        className="h-full w-full object-cover object-top"
                      />
                    </div>

                    <div className="h-[62px] w-[62px] overflow-hidden rounded-[5px] bg-white shadow-sm">
                      <img
                        src={modalImageTryOnTwo}
                        alt="User preview"
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Third card: AI shopping */}
            <div className="hidden overflow-hidden rounded-[5px] bg-[#EFEDEA] p-[6px] md:block md:w-auto">
              <div className="relative h-[330px]">
                <div className="grid h-full grid-cols-2 gap-[5px]">
                  {[
                    modalImageAiOne,
                    modalImageAiTwo,
                    modalImageAiThree,
                    modalImageAiFour,
                  ].map((imageUrl, index) => (
                    <div
                      key={imageUrl}
                      className="relative overflow-hidden rounded-[2px] bg-white"
                    >
                      <img
                        src={imageUrl}
                        alt={`Shopping preview ${index + 1}`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                  ))}
                </div>

                <div className="absolute bottom-1 left-1/2 z-20 flex h-8 -translate-x-1/2 items-center whitespace-nowrap rounded-[3px] bg-white px-3 font-inter text-[9px] font-normal text-[#4A4A4A] shadow-sm">
                  Shop from 100k+ Pieces with AI
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-row text-center justify-center">
          <GradientButton onClick={onNext}>
            {isRestartMode ? "Start Devised Analysis" : "Start free Analysis"}
          </GradientButton>
        </div>
      {showGuestAuthPrompt ? (
        <p className="mt-2 text-center font-helvena text-[12px] text-[#8A8580]">
          No credit card required&nbsp;&nbsp;•&nbsp;&nbsp;Already have an
          account?{" "}
          <button
            type="button"
            onClick={onSignIn}
            className="font-semibold text-[#282828] underline-offset-2 hover:underline"
          >
            Sign In.
          </button>
        </p>
      ) : null}
      </div>
    </StepShell>
  );
}

function BuildProfileStep({ onNext, onSignIn, onClose, showGuestAuthPrompt = true,}) {
  const featureItems = [
    { icon: <VirtualTryOnIcon />, label: "Virtual\nTry-On" },
    { icon: <AskGptIcon />, label: "Ask Gpt" },
    { icon: <SaveToBoardsIcon />, label: "Save to\nBoards" },
    { icon: <FindSimilarItemsIcon />, label: "Find Similar\nitems" },
  ];

  const analysisItems = [
    {
      label: "Color Analysis",
      icon: <ColorAnalysisIcon />,
    },
    {
      label: "Body Shape",
      icon: <BodyShapeIcon />,
    },
    {
      label: "Face Shape",
      icon: <FaceShapeIcon />,
    },
    {
      label: "Height & Proportions",
      icon: <HeightProportionsIcon />,
    },
  ];

  return (
    <StepShell width="max-w-[690px]" onClose={onClose}>
      <div className="px-4 pb-5 pt-8 sm:px-9 sm:pb-8">
        <div className="text-center">
          <h2 className="font-helvena text-[20px] font-medium tracking-[-2%] text-[#313131] sm:text-[24px]">
            Lets build your profile
          </h2>

          {/* Mobile description */}
          <p className="mx-auto mt-2 max-w-[355px] font-helvena text-[11px] leading-[19px] text-[#8A8580] sm:hidden">
            Upload a few photos. Add some measurements — we analyze your body
            proportions, face shape, and seasonal color palette.
          </p>

          {/* Desktop description */}
          <p className="mx-auto mt-2 hidden max-w-[430px] font-helvena text-[12px] leading-5 text-[#8A8580] sm:block">
            Upload your photos. We analyze your proportions, face shape, and
            color analysis.
          </p>
        </div>

        <div
          className={cx(
            "mx-auto mt-8 grid w-full max-w-[400px] grid-cols-1 gap-[10px]",
            "sm:mt-5 sm:grid-cols-2 sm:gap-1.5",
          )}
        >
          {/* Step 1 */}
          <div
            className={cx(
              "relative h-[118px] overflow-hidden rounded-[5px]",
              "sm:row-span-1 sm:h-[230px]",
            )}
          >
            <img
              src={profileUploadImage}
              alt="Upload your photo"
              className="h-full w-full object-cover object-top sm:object-center"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent sm:hidden" />

            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent px-3 pb-3 pt-12 text-white">
              <div className="flex items-center gap-2 font-spaceMono text-[12px]">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#8F333B] font-inter text-[12px] font-bold">
                  1
                </span>

                {/* Mobile label */}
                <span className="leading-[18px] sm:hidden">
                  Upload your photos
                  <br />
                  and measurements
                </span>

                {/* Ddesktop label */}
                <span className="hidden sm:inline">
                  1 STEP Only
                  <br />
                  Upload your photo
                </span>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="relative h-[138px] overflow-hidden rounded-[5px] sm:h-[230px]">
            <img
              src={profileAnalysisImage}
              alt="Profile analysis"
              className="h-full w-full object-cover object-top sm:object-center"
            />

            <div className="absolute inset-0 bg-black/20 sm:hidden" />

            <div className="absolute left-3 top-3 flex items-start gap-2 font-spaceMono text-[12px] text-white">
              <span
                className={cx(
                  "flex h-6 w-6 shrink-0 items-center justify-center",
                  "rounded-full bg-[#8F333B] font-inter text-[12px] font-bold",
                  "sm:mt-[10px]",
                )}
              >
                2
              </span>

              <span className="leading-[18px] sm:text-[14px]">
                We analyze your
                <br />
                features
              </span>
            </div>

            <div
              className={cx(
                "absolute bottom-2 left-3 right-3",
                "grid grid-cols-2 gap-x-3 gap-y-0",
                "rounded-[4px] bg-white px-3 py-2 shadow",
                "sm:left-2 sm:right-2 sm:block sm:px-3 sm:py-2.5",
              )}
            >
              {analysisItems.map(({ label, icon }) => (
                <div
                  key={label}
                  className={cx(
                    "flex min-w-0 items-center gap-2 py-[3px]",
                    "font-helvena text-[10px] text-[#111827]",
                    "sm:py-1 sm:text-[12px]",
                  )}
                >
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center text-[#8F333B]">
                    {icon}
                  </span>

                  <span className="whitespace-nowrap">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Step 3 */}
          <div className="relative h-[62px] overflow-hidden rounded-[5px] sm:h-[230px]">
            <img
              src={profileShirtImage}
              alt="Try the platform"
              className="h-full w-full object-cover object-top sm:object-center"
            />

            <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/30 to-transparent sm:hidden" />

            <div
              className={cx(
                "absolute inset-x-0 bottom-0 h-full",
                "flex items-center px-3 text-white",
                "sm:h-auto sm:block sm:bg-gradient-to-t",
                "sm:from-black/75 sm:to-transparent sm:pb-3 sm:pt-12",
              )}
            >
              <div className="flex items-center gap-2 font-spaceMono text-[11px] sm:text-[12px]">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#8F333B] font-inter text-[12px] font-bold">
                  3
                </span>

                <span className="sm:hidden">Access Matches and many more</span>
                <span className="hidden sm:inline">Try the platform</span>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="grid grid-cols-2 gap-[6px] sm:gap-1.5">
            {featureItems.map((item) => (
              <div
                key={item.label}
                className={cx(
                  "grid h-[48px] grid-cols-[34px_minmax(0,1fr)]",
                  "items-center rounded-[4px] border border-transparent",
                  "bg-[#F8F8F8] px-3",
                  "sm:flex sm:h-auto sm:min-h-[112px]",
                  "sm:flex-col sm:justify-center sm:border-[#E5E5E5]",
                  "sm:bg-[#FAFAFA] sm:px-2 sm:text-center",
                )}
              >
                <span className="flex h-6 w-6 items-center justify-center text-[#A93443]">
                  {item.icon}
                </span>

                {/* Mobile label */}
                <span className="whitespace-nowrap text-center font-spaceMono text-[8px] text-[#252525] sm:hidden">
                  {item.label.replace("\n", " ")}
                </span>

                {/* Desktop label */}
                <span className="mt-1 hidden whitespace-pre-line font-spaceMono text-[9px] text-[#252525] sm:block">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="mx-auto mt-5 max-w-[410px] sm:mt-11">
          <button
            type="button"
            onClick={onNext}
            className={cx(
              "flex h-[42px] w-full items-center justify-center",
              "rounded-[5px]",
              "bg-[linear-gradient(100deg,#ECB1A4_0%,#D26068_62%,#C68D95_100%)]",
              "font-helvena text-[12px] font-normal text-white",
              "transition hover:brightness-[0.98]",
              "sm:mt-4 sm:text-[14px]",
            )}
          >
            <span>Continue</span>
          </button>

        {showGuestAuthPrompt ? (
          <p className="mt-3 whitespace-nowrap text-center font-helvena text-[10px] text-[#8A8580] sm:text-[12px]">
            No credit card required&nbsp;&nbsp;·&nbsp;&nbsp;Already have an
            account?{" "}
            <button
              type="button"
              onClick={onSignIn}
              className="font-semibold text-[#282828] hover:underline"
            >
              Sign In.
            </button>
          </p>
        ) : null}
        </div>
      </div>
    </StepShell>
  );
}


function MeasurementsStep({ initialProfile, onAnalyze, onSignIn, onClose, isAuthenticated = false }) {

   return (
    <StepShell width="max-w-[690px]" onClose={onClose}>
      <ProfileMeasurementsForm
        initialProfile={initialProfile}
        onAnalyze={onAnalyze}
        onSignIn={onSignIn}
        isAuthenticated={isAuthenticated}
      />
    </StepShell>
  );
}

function AnalysisRow({ label, value }) {
  return (
    <div>
      <div className="flex items-center justify-between font-spaceMono text-[10px] font-bold uppercase text-white">
        <span>{label}</span>

        <span className="text-[#8F333B]">{value}%</span>
      </div>
      <div className="mt-2 h-[2px] bg-[#373737]">
        <div
          className="h-full bg-[#8F333B] transition-[width] duration-150"
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}

function AnalyzingStep({ profile, progress, statusText, onClose }) {
  return (
    <StepShell dark width="max-w-[690px]" onClose={onClose}>
      <div className="flex min-h-[500px] flex-col items-center justify-center px-6 py-12 text-white sm:min-h-[648px]">
        <div className="flex h-12 w-12 items-center justify-center rounded-[5px] bg-gradient-to-b from-[#F0BEC0] to-[#AF3647] font-spaceMono text-[20px] shadow-[0_0_34px_rgba(174,54,71,0.42)]">
          <EvenessLogo className="h-[18px] w-[19px] lg:h-[19px] lg:w-[21px]" />
        </div>

        <h2 className="mt-16 font-helvena text-[32px] font-normal tracking-[-0.04em]">
          Analyzing your profile...
        </h2>
        <p className="mt-2 text-center font-spaceMono font-normal text-[12px] tracking-[0.08em] text-[#B8B8BB]">
          {statusText || "This takes just a moment"}
        </p>

        <div className="mt-8 w-full max-w-[335px] space-y-7">
          <AnalysisRow label="Body Proportions" value={progress.body} />
          <AnalysisRow label="Color Palette" value={progress.palette} />
          <AnalysisRow label="Face Shape" value={progress.face} />
        </div>

        <div className="mt-auto grid grid-cols-3 gap-1.5 pt-6">
          <div className="flex h-[42px] min-w-[70px] items-center justify-center rounded-[5px] bg-[#8F333B] px-3 font-spaceMono text-[12px]">
            {profile.gender || "Female"}
          </div>
          <div className="flex h-[42px] min-w-[70px] items-center justify-center rounded-[5px] bg-white px-3 font-spaceMono text-[12px] text-[#3D3A37] border border-[#D9D5D0]">
            {formatCmToFeetInches(profile.height || 170)}
          </div>
          <div className="flex h-[42px] min-w-[70px] items-center justify-center rounded-[5px] bg-white px-3 font-spaceMono text-[12px] text-[#3D3A37] border border-[#D9D5D0]">
            {profile.weight || 70} KG
          </div>
        </div>
        <p className="mt-[10px] text-center font-spaceMono text-[12px] leading-4 text-[#B8B8BB] max-w-[220px]">
          You can change measurements after logging in too
        </p>
      </div>
    </StepShell>
  );
}

function MatchesStep({
  profile,
  products,
  onNext,
  onSignIn,
  onClose,
  isRestartMode = false,
  showGuestAuthPrompt = true,
}) {
  const analysis = profile?.analysis || {};

  const tags = [
    analysis?.gender
      ? analysis.gender.charAt(0).toUpperCase() + analysis.gender.slice(1)
      : profile?.gender,
    analysis?.body_type ? `${analysis.body_type} Body Shape` : null,
    analysis?.face_shape ? `${analysis.face_shape} Face Shape` : null,
    analysis?.seasonal_color,
  ].filter(Boolean);

  return (
    <StepShell dark width="max-w-[700px]" onClose={onClose}>
      <div className="pb-7 pt-8">
        <div className="px-4 text-center sm:px-8">
          <h2 className="font-helvena text-[24px] font-medium tracking-[-2%] text-white">
            {isRestartMode
              ? "Your new profile is ready."
              : "Your matches are ready."}
          </h2>
          <p className="mt-2 font-helvena text-[12px] text-[#8A8580]">
            {isRestartMode
              ? "Continue to see your matches across 100,000+ pieces and try them on."
              : "Sign up to see your matches across 100,000+ pieces and try them on."}
          </p>
          <div className="mt-5 flex gap-[5px] overflow-x-auto pb-1">
            {tags.map((tag, index) => (
              <div
                key={tag}
                className={cx(
                  "flex h-[42px] shrink-0 items-center justify-center rounded-[5px] px-6 font-spaceMono text-[12px] text-white",
                  index === 0 ? "bg-[#8F333B]" : "bg-[#30302E]",
                )}
              >
                {tag}
              </div>
            ))}
          </div>
        </div>

        <div className="px-[20px] md:px-[68px]">
          <div className="mt-6 flex gap-3 overflow-x-auto pb-4">
            {products.length > 0 ? (
              products.map((product) => (
                <article
                  key={product.id}
                  className="w-[181px] shrink-0 overflow-hidden bg-transparent text-white"
                >
                  <div className="relative h-[242px] overflow-hidden rounded-[5px] bg-[#EFEFEF]">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="h-full w-full object-cover"
                    />

                    <div className="absolute bottom-2 left-2 right-2 flex items-center gap-1">
                      {product.matchPercentage !== null ? (
                        <span className="h-[19px] rounded-[3px] bg-white px-[5px] py-1 font-spaceMono text-[10px] text-[#8F333B]">
                          {product.matchPercentage}% Match
                        </span>
                      ) : null}

                      {product.recommendedSizeLabel ? (
                        <span className="h-[19px] rounded-[3px] bg-white px-[5px] pt-[3px] font-spaceMono text-[10px] text-[#8F333B]">
                          {product.recommendedSizeLabel}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="px-0 py-2">
                    <div className="font-spaceMono text-[10px] font-bold text-white">
                      Our Legacy
                    </div>

                    <div className="mt-1 flex items-center justify-between font-helvena text-[#BFBFBF]">
                      <span className="text-[#C5C5C5] text-[14px]">
                        {product.brand || product.name}
                      </span>
                      <span className="text-[12px] text-white">
                        {product.price}
                      </span>
                    </div>
                  </div>{" "}
                </article>
              ))
            ) : (
              <div className="flex min-h-[236px] w-full items-center justify-center px-6 text-center font-inter text-[13px] text-[#888888]">
                No personalized products were returned.
              </div>
            )}
          </div>

          <div className="mx-auto mt-5 max-w-[564px] rounded-[5px] bg-[#242424] px-5 py-5 md:py-[10px]">
            <div className="grid grid-cols-4 gap-2">
              {[
                "Match% for 100k+ products",
                "Sizing",
                "Virtual Try-\non",
                "Eveness GPT",
              ].map((item) => (
                <div
                  key={item}
                  className="relative flex h-[73px] items-center justify-center rounded-[5px] bg-[#30302E] px-3 text-center font-spaceMono text-[10px] font-bold text-white"
                >
                  <span className="whitespace-pre-line">{item}</span>
                </div>
              ))}
            </div>

            <button
              className={cx(
                "mt-4 flex h-[42px] w-full items-center justify-center",
                "rounded-[5px]",
                "bg-[linear-gradient(100deg,#ECB1A4_0%,#D26068_62%,#C68D95_100%)]",
                "font-helvena text-[14px] font-normal text-white",
                "transition hover:brightness-[0.98]",
              )}
              onClick={onNext}
            >
              {isRestartMode ? "Finish" : "Complete by Signing Up"}
            </button>

            {!isRestartMode && showGuestAuthPrompt ? (
              <p className="mt-4 text-center font-inter text-[13px] text-[#8E8E8E]">
                No credit card required&nbsp;&nbsp;•&nbsp;&nbsp;Already have an
                account?{" "}
                <button
                  type="button"
                  onClick={onSignIn}
                  className="text-[#A8A8A8] hover:underline"
                >
                  Sign In.
                </button>
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </StepShell>
  );
}

function SaveFashionEngineStep({
  onContinue,
  onSignIn,
  onClose,
  isGoogleRedirecting = false,
  title = "See all your matches",
  showGuestAuthPrompt = true,
}) {
  return (
    <StepShell width="max-w-[585px]" onClose={onClose}>
      <div className="px-5 pb-6 pt-6 sm:px-10 sm:pb-7 sm:pt-7">
        <div className="text-center">
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-[5px] bg-gradient-to-b from-[#F2D7D3] via-[#DD9B95] to-[#B74755] font-spaceMono text-[18px] text-white shadow-sm">
            <EvenessLogo className="h-[18px] w-[19px] lg:h-[19px] lg:w-[21px]" />
          </div>

          <h2 className="mt-4 font-inter text-[22px] font-semibold tracking-[-0.04em] text-[#303030] sm:text-[24px]">
            {title}
          </h2>

          <p className="mx-auto mt-1 max-w-[390px] font-inter text-[11px] leading-[1.55] text-[#999999] sm:text-[12px]">
            Your matches are saved. Create a free account to see all of them and
            try them on.
          </p>
        </div>

        <div
          className="mx-auto mt-3 h-[245px] w-full max-w-[335px] overflow-hidden rounded-[5px] border border-[#E2DEDB] bg-[#F8F6F4] sm:h-[295px]">
          <img
            src={seeAllMatches}
            alt="See all matches"
            className="h-full w-[calc(100%+2px)] max-w-none -translate-x-px object-cover"
          />
        </div>
        <div className="mx-auto mt-2 w-full max-w-[335px] space-y-2">
          <GradientButton
            onClick={() => onContinue("google")}
            className="h-10 text-[13px] w-full"
          >
            <span className="mr-[10px]">
              <GoogleIcon />
            </span>{" "}
            <span className="whitespace-nowrap">Continue with Google</span>
          </GradientButton>
          <button
            type="button"
            className="flex h-10 w-full items-center justify-center rounded-[5px] border border-[#DDD8D4] bg-white px-4 font-inter text-[13px] font-medium text-[#171717] transition hover:bg-[#FAFAFA] disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => onContinue("email")}
          >
            Continue with Email
          </button>
        </div>

      {showGuestAuthPrompt ? (
        <p className="mt-3 text-center font-helvena text-[10px] text-[#8A8580] sm:text-[12px] font-normal">
          No credit card required&nbsp;&nbsp;•&nbsp;&nbsp;Already have an
          account?{" "}
          <button
            type="button"
            onClick={onSignIn}
            className="font-semibold text-[#282828] hover:underline"
          >
            Sign In.
          </button>
        </p>
      ) : null}
      </div>
    </StepShell>
  );
}

const HomeProfilePopupFlow = ({
  isOpen,
  onClose,
  onSignIn,
  onSignUp,
  isAuthenticated: isAuthenticatedProp,
  isGoogleRedirecting = false,
  mode = "public", // "public" | "restart"
  onRestartComplete,
  initialStep = 0,
  initialProfile = null,
  autoAnalyzeOnOpen = false,
  saveStepTitle = "See all your matches",
}) => {
  const isAuthenticated =
    typeof isAuthenticatedProp === "boolean"
      ? isAuthenticatedProp
      : typeof window !== "undefined"
        ? Boolean(localStorage.getItem("authToken"))
        : false;

  const showGuestAuthPrompt = !isAuthenticated;
  const isRestartMode = mode === "restart";
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState({
    gender: "Female",
    height: 170,
    weight: 70,
  });
  const [progress, setProgress] = useState({ body: 0, palette: 0, face: 0 });

  const [analyzePublicFace] = usePostPublicAnalyzeFaceMutation();
  const [analyzePrivateFace] = usePostAnalyzeFaceMutation();
  const [storePrivateMeasurements] =
    useProfileMeasurementsStoreUpdateMutation();

  const [postUploadImage] = usePostPublicUploadImageMutation();
  const [analyzeFace] = usePostPublicAnalyzeFaceMutation();
  const [storePublicMeasurements] =
    usePublicProfileMeasurementsStoreUpdateMutation();
  const [getSurveySuggestions] = usePostMarketplaceSurveySuggestionsMutation();

  const [analysisStage, setAnalysisStage] = useState("");
  const [suggestionProducts, setSuggestionProducts] = useState([]);
  const [publicSurveyToken, setPublicSurveyToken] = useState("");

  const handleAnalyzeProfile = useCallback(
    async (nextProfile) => {
      setProfile(nextProfile);
      setSuggestionProducts([]);
      setAnalysisStage("Uploading your photos...");
      setProgress({
        body: 8,
        palette: 5,
        face: 5,
      });
      setStep(3);

      try {
        const [faceImageUrls, bodyShotUrl, angleImageUrls] = await Promise.all([
          Promise.all(
            (nextProfile.faceFiles || [nextProfile.faceFile])
              .filter(Boolean)
              .map((file) => uploadToS3(file, postUploadImage)),
          ),
          uploadToS3(nextProfile.fullBodyFile, postUploadImage),
          Promise.all(
            (nextProfile.angleFiles || []).map((file) =>
              uploadToS3(file, postUploadImage),
            ),
          ),
        ]);

        const faceImageUrl = faceImageUrls[0];
        const gender = normalizePublicGender(nextProfile.gender);

        setAnalysisStage("Analyzing your face, colors, and body shape...");

        setProgress({
          body: 30,
          palette: 25,
          face: 20,
        });

        const analyzeResponse = await (
          isRestartMode
            ? analyzePrivateFace({
                faceAnalysis: {
                  image_url: faceImageUrl,
                  body_shot_url: bodyShotUrl,
                  gender,
                },
              })
            : analyzePublicFace({
                publicFaceAnalysis: {
                  image_url: faceImageUrl,
                  body_shot_url: bodyShotUrl,
                  gender,
                },
              })
        ).unwrap();

        const initialToken = isRestartMode
          ? ""
          : analyzeResponse?.token;

        if (!isRestartMode && !initialToken) {
          throw new Error(
            "The face analysis response did not contain a token.",
          );
        }

        const waistSize =
          analyzeResponse?.waist_size ?? analyzeResponse?.waist ?? null;

        const clothingSize = analyzeResponse?.clothing_size ?? null;

        const measurementPayload = {
          measurement_system: "imperial",
          height: convertCmToImperialHeight(nextProfile.height),
          weight: convertKgToImperialWeight(nextProfile.weight),
          waist_size:
            waistSize !== null && waistSize !== undefined
              ? String(waistSize)
              : null,
          clothing_size:
            clothingSize !== null && clothingSize !== undefined
              ? String(clothingSize)
              : null,
          survey_headshot_url: faceImageUrl,
          survey_full_body_url: bodyShotUrl,
          survey_body_angle_urls: angleImageUrls,
        };

        const measurementResponse = await (
          isRestartMode
            ? storePrivateMeasurements({
                profileMeasurement: measurementPayload,
              })
            : storePublicMeasurements({
                guestSurveyMeasurement: {
                  token: initialToken,
                  ...measurementPayload,
                },
              })
        ).unwrap();

        const finalToken = isRestartMode
          ? ""
          : measurementResponse?.token || initialToken;

        if (!isRestartMode) {
          if (!finalToken) {
            throw new Error(
              "The profile measurement response did not contain a survey token.",
            );
          }
          savePendingSurveyToken(finalToken);
          setPublicSurveyToken(finalToken);
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("eveness-survey-token-changed", {
                detail: {
                  token: finalToken,
                },
              }),
            );
          }
        }

        const combinedAnalysis = {
          ...analyzeResponse,
          ...measurementResponse,
          ...(!isRestartMode && finalToken
            ? { token: finalToken }
            : {}),
          survey_headshot_url:
            measurementResponse?.survey_headshot_url || faceImageUrl,
          survey_full_body_url:
            measurementResponse?.survey_full_body_url || bodyShotUrl,
        };

        setProfile({
          ...nextProfile,
          ...(!isRestartMode && finalToken
            ? { token: finalToken }
            : {}),
          faceImageUrl,
          faceImageUrls,
          bodyShotUrl,
          angleImageUrls,
          analysis: combinedAnalysis,
        });

        setAnalysisStage("Finding your personalized matches...");

        setProgress({
          body: 85,
          palette: 80,
          face: 75,
        });
        const suggestionsResponse = await getSurveySuggestions({
          marketplaceSurveySuggestionRequest: {
            user_data: {
              face_shape: combinedAnalysis?.face_shape || "",
              body_type: combinedAnalysis?.body_type || "",
              seasonal_color: combinedAnalysis?.seasonal_color || "",
              gender: normalizePublicGender(combinedAnalysis?.gender || gender),
              waist: String(
                combinedAnalysis?.waist_size ??
                  combinedAnalysis?.waist ??
                  waistSize ??
                  "",
              ),
              clothing_size: String(
                combinedAnalysis?.clothing_size ?? clothingSize ?? "",
              ),
            },
          },
        }).unwrap();

        setSuggestionProducts(
          normalizeSuggestionProducts(suggestionsResponse?.products),
        );

        setProgress({
          body: ANALYSIS_TARGETS.body,
          palette: ANALYSIS_TARGETS.palette,
          face: ANALYSIS_TARGETS.face,
        });

        setAnalysisStage("");
        setStep(4);
      } catch (error) {
        console.error("Public profile analysis failed:", error);

        toast.error(getProfileApiError(error));

        setAnalysisStage("");

        setProgress({
          body: 0,
          palette: 0,
          face: 0,
        });

        setStep(2);
      }
    },
    [
      isRestartMode,
      analyzePublicFace,
      analyzePrivateFace,
      storePublicMeasurements,
      storePrivateMeasurements,
      getSurveySuggestions,
      postUploadImage,
    ],
  );

  const autoAnalyzeStartedRef = useRef(false);
  useEffect(() => {
    const shouldAutoAnalyze =
      isOpen &&
      initialStep === 3 &&
      autoAnalyzeOnOpen &&
      Boolean(initialProfile);

    if (!shouldAutoAnalyze) {
      autoAnalyzeStartedRef.current = false;
      return;
    }

    if (autoAnalyzeStartedRef.current) return;

    autoAnalyzeStartedRef.current = true;
    handleAnalyzeProfile(initialProfile);
  }, [isOpen, initialStep, autoAnalyzeOnOpen, initialProfile, handleAnalyzeProfile ]);

  const handleSignUp = useCallback(
    (provider) => {
      const savedToken =
        publicSurveyToken || profile?.token || getPendingSurveyToken();

      if (savedToken) {
        savePendingSurveyToken(savedToken);
      }

      const profileSummary = {
        gender: profile.gender,
        height: profile.height,
        weight: profile.weight,
        token: savedToken,
      };

      try {
        sessionStorage.setItem(
          "personal_fashion_engine_signup",
          JSON.stringify({
            provider,
            token: savedToken,
            profile: profileSummary,
          }),
        );
      } catch {}

      onSignUp?.({
        provider,
        token: savedToken,
        profile,
      });
    },
    [onSignUp, profile, publicSurveyToken],
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    setStep(initialStep);
    setProgress({ body: 0, palette: 0, face: 0 });
    setSuggestionProducts([]);
    setAnalysisStage("");
    setProfile(
      initialProfile ?? {
        gender: "Female",
        height: 170,
        weight: 70,
      }
    );
  }, [isOpen, initialStep, initialProfile]);

  useEffect(() => {
    if (step !== 3) return undefined;

    const interval = window.setInterval(() => {
      setProgress((current) => {
        const next = {
          body: Math.min(
            ANALYSIS_TARGETS.body,
            current.body + Math.random() * 2.6 + 0.8,
          ),
          palette: Math.min(
            ANALYSIS_TARGETS.palette,
            current.palette + Math.random() * 1.8 + 0.5,
          ),
          face: Math.min(
            ANALYSIS_TARGETS.face,
            current.face + Math.random() * 1.4 + 0.4,
          ),
        };

        return {
          body: Math.round(next.body),
          palette: Math.round(next.palette),
          face: Math.round(next.face),
        };
      });
    }, 180);

    return () => window.clearInterval(interval);
  }, [step]);

  const content = useMemo(() => {
    if (step === 0) {
      return (
        <HeroStep
          onNext={() => setStep(isRestartMode ? 2 : 1)}
          onSignIn={onSignIn}
          onClose={onClose}
          isRestartMode={isRestartMode}
          showGuestAuthPrompt={showGuestAuthPrompt}
        />
      );
    }

    if (step === 1) {
      return (
        <BuildProfileStep
          onNext={() => setStep(2)}
          onSignIn={onSignIn}
          onClose={onClose}
          showGuestAuthPrompt={showGuestAuthPrompt}
        />
      );
    }

    if (step === 2) {
      return (
        <MeasurementsStep
          initialProfile={profile}
          onSignIn={onSignIn}
          onClose={onClose}
          onAnalyze={handleAnalyzeProfile}
          isAuthenticated={isAuthenticated}
        />
      );
    }

    if (step === 3) {
      return (
        <AnalyzingStep
          profile={profile}
          progress={progress}
          statusText={analysisStage}
          onClose={onClose}
        />
      );
    }
    if (step === 4) {
      return (
        <MatchesStep
          profile={profile}
          products={suggestionProducts}
          onNext={async () => {
            if (isRestartMode) {
              await onRestartComplete?.();
              onClose?.();
              return;
            }

            setStep(5);
          }}
          onSignIn={onSignIn}
          onClose={onClose}
          isRestartMode={isRestartMode}
          showGuestAuthPrompt={showGuestAuthPrompt}
        />
      );
    }

    return (
      <SaveFashionEngineStep
        title={saveStepTitle}
        onContinue={handleSignUp}
        onSignIn={onSignIn}
        onClose={onClose}
        isGoogleRedirecting={isGoogleRedirecting}
        showGuestAuthPrompt={showGuestAuthPrompt}
      />
    );
  }, [
    step,
    onSignIn,
    onClose,
    profile,
    progress,
    analysisStage,
    suggestionProducts,
    handleAnalyzeProfile,
    handleSignUp,
    isGoogleRedirecting,
    saveStepTitle,
    showGuestAuthPrompt
  ]);
  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div
      className={[
        "fixed inset-0 z-[2147483646]",
        "flex items-center justify-center",
        "overflow-hidden px-3 py-4 sm:px-5 sm:py-10",
        "bg-black/45 backdrop-blur-xs",
      ].join(" ")}
      role="dialog"
      aria-modal="true"
      aria-label="Build your Eveness profile"
    >
      <div className="flex h-full min-h-0 w-full items-center justify-center">
        {content}
      </div>
    </div>,
    document.body,
  );
};

export default HomeProfilePopupFlow;
